import { useMemo } from "react";

import type { SWReactIconProps } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import type { IPlainTooltipProps } from "@similarweb/ui-components";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { StyledItem, StyledText } from "./styles";

type ExportVariantsDropdownItemProps = Partial<Pick<SWReactIconProps, "iconColor" | "iconName">> & {
    isUseLockedIcon?: boolean;
    isDisabled?: boolean;
    text: string;
    tooltipContent?: IPlainTooltipProps["tooltipContent"];
    tooltipDisabledContent?: IPlainTooltipProps["tooltipContent"];
    variant: string;
    onClick?(): void;
};

const ExportVariantsDropdownItem = (props: ExportVariantsDropdownItemProps) => {
    const {
        iconName,
        iconColor,
        isDisabled = false,
        isUseLockedIcon = true,
        variant,
        text,
        tooltipContent,
        tooltipDisabledContent,
        onClick,
    } = props;
    const hasClickHandler = typeof onClick === "function";

    const tooltipProps = useMemo(() => {
        if (isDisabled) {
            return {
                enabled: typeof tooltipDisabledContent !== "undefined",
                content: tooltipDisabledContent,
            };
        }

        return {
            enabled: typeof tooltipContent !== "undefined",
            content: tooltipContent,
        };
    }, [isDisabled, tooltipDisabledContent, tooltipContent]);

    const handleClick = () => {
        if (!isDisabled && hasClickHandler) {
            onClick();
        }
    };

    return (
        <Tooltip {...tooltipProps}>
            <StyledItem
                canBeHovered={hasClickHandler}
                isDisabled={isDisabled}
                data-automation="export-variant-item"
                data-automation-variant={variant}
                onClick={handleClick}
            >
                <Flex alignItems={"center"}>
                    {iconName ? (
                        <SWReactIcons
                            iconName={iconName}
                            iconColor={iconColor}
                            size="sm"
                        />
                    ) : null}
                    <StyledText
                        hasExtendedMargin={!iconName}
                        data-automation="export-variant-item.text"
                    >
                        {text}
                    </StyledText>
                </Flex>

                {isUseLockedIcon && isDisabled ? (
                    <SWReactIcons
                        iconName="locked2"
                        size="sm"
                    />
                ) : null}
            </StyledItem>
        </Tooltip>
    );
};

export default ExportVariantsDropdownItem;
